import {computed, makeObservable, observable} from "mobx";
import type {SpaceViewRenderer} from "../renderers/SpaceViewRenderer";
import type {Xyicon3D} from "../elements3d/Xyicon3D";
import type {SpaceItem} from "../elements3d/SpaceItem";
import type {Xyicon} from "../../../../../../data/models/Xyicon";
import type {Link} from "../../../../../../data/models/Link";
import {XHRLoader} from "../../../../../../utils/loader/XHRLoader";
import {
	ArrowStyle,
	LineStyle,
	LinkDirection,
	XyiconFeature,
	type LinkBasicDto,
	type LinkXyiconRequest,
	type PointDouble,
	type XyiconLinkDetail,
} from "../../../../../../generated/api/base";

export class LinkManager {
	private _spaceViewRenderer: SpaceViewRenderer;

	@observable
	private _isInLinkMode: boolean = false;
	private _fromPortId: string = null;
	private _endXyicon: Xyicon | null = null;

	constructor(spaceViewRenderer: SpaceViewRenderer) {
		makeObservable(this);
		this._spaceViewRenderer = spaceViewRenderer;
	}

	public enableLinkMode(fromPortID: string) {
		this._fromPortId = fromPortID;
		this._isInLinkMode = true;
		this._spaceViewRenderer.spaceItemController.closePortSelector();
	}

	public disableLinkMode = () => {
		this._isInLinkMode = false;
		this._spaceViewRenderer.spaceItemController.closePortSelector();
		this._spaceViewRenderer.spaceItemController.updateActionBar();
	};

	/** When a user selects the endxyicon of a link */
	public onEndXyiconClickInLinkMode(xyicon: Xyicon, portsPosition: PointDouble = xyicon.position) {
		this._endXyicon = xyicon;
		const spaceItemController = this._spaceViewRenderer.spaceItemController;
		const ports = this._endXyicon.ports;

		if (ports.length > 0) {
			spaceItemController.openPortSelector(portsPosition.x, portsPosition.y, this._endXyicon, ports, "to");
		} else {
			this.createLinks(null);
			this.disableLinkMode();
		}
	}

	/** Create links between the previously selected xyicons (from) and the lastly clicked xyicon
	 * toPortID: if null, the link is created to the xyicon itself
	 */
	public createLinks(toPortId: string | null) {
		const selectedItems = this._spaceViewRenderer.xyiconManager.selectedItems;
		const linkDetails: XyiconLinkDetail[] = selectedItems.map((xyicon: Xyicon3D) => {
			return {
				objectAID: xyicon.modelData.id,
				objectAPortID: this._fromPortId,
				objectBID: this._endXyicon.id,
				objectBPortID: toPortId,
				isEmbedded: false,
				direction: LinkDirection.Both,
				settings: {
					lineColor: "#1E88E5",
					lineWidth: 4,
					arrowStyle: ArrowStyle.Filled,
					lineStyle: LineStyle.Solid,
				},
			};
		});

		const linkParams: LinkXyiconRequest = {
			objectAPortfolioID: selectedItems[0]?.modelData.portfolioId,
			objectBPortfolioID: this._endXyicon.portfolioId,
			xyiconLinkDetails: linkDetails,
		};

		return this.sendCreateRequest(linkParams);
	}

	public embedXyiconsIntoParentXyicon(xyiconsToEmbed: Xyicon3D[], parentXyicon: Xyicon3D) {
		// xyiconsToEmbed is usually the selecteditems, but the items get deselected when the visibility is set to false, so we better clone this array for safety
		xyiconsToEmbed = [...xyiconsToEmbed];

		this._spaceViewRenderer.spaceItemController.deselectAll();

		const linkDetails: XyiconLinkDetail[] = [];

		for (const xyiconToEmbed of xyiconsToEmbed) {
			xyiconToEmbed.setVisibility(false);

			// Check if this xyicon already has a link to the parent
			const existingLinks = this._spaceViewRenderer.actions.getLinksXyiconXyicon(xyiconToEmbed.modelData.id);
			const existingLinkToParent = existingLinks.find(
				(linkObj) =>
					(linkObj.link.objectAID === parentXyicon.modelData.id && linkObj.link.objectBID === xyiconToEmbed.modelData.id) ||
					(linkObj.link.objectAID === xyiconToEmbed.modelData.id && linkObj.link.objectBID === parentXyicon.modelData.id),
			);

			if (existingLinkToParent) {
				// Convert existing link to embedded link by preserving settings but ensuring correct parent-child structure
				const existingLink = existingLinkToParent.link;

				linkDetails.push({
					objectAID: parentXyicon.modelData.id, // Parent is always objectA
					objectAPortID: null,
					objectBID: xyiconToEmbed.modelData.id, // Child is always objectB
					objectBPortID: null,
					isEmbedded: true,
					direction: LinkDirection.AtoB, // Always AtoB for embedding
					settings: existingLink.linkProperties?.settings, // Preserve existing visual settings
				});
			} else {
				// Create new embedded link for brand new xyicon with default settings
				linkDetails.push({
					objectAID: parentXyicon.modelData.id,
					objectAPortID: null,
					objectBID: xyiconToEmbed.modelData.id,
					objectBPortID: null,
					isEmbedded: true,
					direction: LinkDirection.AtoB, // Always AtoB for embedding
					settings: {
						lineColor: "#1E88E5",
						lineWidth: 4,
						arrowStyle: ArrowStyle.Filled,
						lineStyle: LineStyle.Solid,
					},
				});
			}
		}

		const params: LinkXyiconRequest = {
			objectAPortfolioID: this._spaceViewRenderer.transport.appState.activePortfolioId,
			objectBPortfolioID: this._spaceViewRenderer.transport.appState.activePortfolioId,
			xyiconLinkDetails: linkDetails,
		};

		return this.sendCreateRequest(params);
	}

	public unembedXyicons(xyiconsToUnembed: Xyicon3D[], currentParentXyicon: Xyicon) {
		const linkDetails: XyiconLinkDetail[] = [];

		for (const xyicon3D of xyiconsToUnembed) {
			// Get the existing embedded link to preserve its properties
			const existingLinks = this._spaceViewRenderer.actions.getLinksXyiconXyicon(xyicon3D.modelData.id);
			const existingEmbeddedLink = existingLinks.find(
				(linkObj) =>
					linkObj.link.isEmbedded &&
					((linkObj.link.objectAID === currentParentXyicon.id && linkObj.link.objectBID === xyicon3D.modelData.id) ||
						(linkObj.link.objectAID === xyicon3D.modelData.id && linkObj.link.objectBID === currentParentXyicon.id)),
			);

			linkDetails.push({
				objectAID: currentParentXyicon.id, // Parent is always objectA
				objectAPortID: null,
				objectBID: xyicon3D.modelData.id, // Child is always objectB
				objectBPortID: null,
				isEmbedded: false,
				direction: LinkDirection.AtoB, // Always AtoB for unembedding
				settings: existingEmbeddedLink?.link.linkProperties?.settings || {
					// Preserve existing settings
					lineColor: "#1E88E5",
					lineWidth: 4,
					arrowStyle: ArrowStyle.Filled,
					lineStyle: LineStyle.Solid,
				},
			});
		}

		return this.sendCreateRequest({
			objectAPortfolioID: this._spaceViewRenderer.transport.appState.activePortfolioId,
			objectBPortfolioID: this._spaceViewRenderer.transport.appState.activePortfolioId,
			xyiconLinkDetails: linkDetails,
		});
	}

	public async sendCreateRequest(linkParams: LinkXyiconRequest) {
		if (linkParams.xyiconLinkDetails.length > 0) {
			const result = await this._spaceViewRenderer.transport.requestForOrganization<LinkBasicDto>({
				url: "xyicons/createlink",
				method: XHRLoader.METHOD_PUT,
				params: linkParams,
			});

			// Manual update of local link list - add the created links immediately
			if (result?.result && Array.isArray(result.result)) {
				const {actions} = this._spaceViewRenderer.transport.appState;
				for (const linkData of result.result as LinkBasicDto[]) {
					if (!actions.getFeatureItemById<Link>(linkData.linkID, XyiconFeature.Link)) {
						actions.addToList(linkData, XyiconFeature.Link);
					}
				}
			}

			// Manual update to ensure immediate link icon appearance
			this._spaceViewRenderer.spaceItemController.linkIconManager.update();

			return result;
		}
	}

	public isXyiconLinkableToSelectedXyicons(xyicon: SpaceItem) {
		return (
			xyicon &&
			xyicon.spaceItemType === "xyicon" &&
			this._spaceViewRenderer.xyiconManager.selectedItems.length > 0 &&
			!this._spaceViewRenderer.xyiconManager.selectedItems.includes(xyicon)
		);
	}

	@computed
	public get isInLinkMode() {
		return this._isInLinkMode;
	}
}
